import { useDispatch, useSelector } from "react-redux";
import { Fragment, useEffect, useMemo, useState } from "react";
import { API } from "../../axios/instance";
import { Loader } from "../../components/Loader";
import { Breadcrumb } from "../../components/Breadcrumb";
import { fetchContacts } from "../../redux/contactStore/action";
import { fetchFiles, uploadFile, getFileById } from "../../redux/fileStoreStore/action";
import { useNavigate } from "react-router-dom";
import "./DashboardMain.css";
import { FileUploadModal } from "../../components/FileUploadModal";
import { getUserContext } from "../../utils/auth";

const FALLBACK_USER_ID = "6897acb7f2bc1479c645102d";
const DEFAULT_BUSINESS_ID = "6897b362dce30c2c9892b759";

export function DashboardMain() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const auth = useSelector((s) => s.auth);
  const contactStatus = useSelector((s) => s.contact.status);
  const fileStatus = useSelector((s) => s.fileStore.status);
  const rawContacts = useSelector((s) => s.contact.data);
  const contacts = useMemo(() => rawContacts || [], [rawContacts]);
  const rawFiles = useSelector((s) => s.fileStore.data);
  const files = useMemo(() => rawFiles || [], [rawFiles]);
  const userContext = getUserContext();
  const effectiveUserId = userContext.userId || FALLBACK_USER_ID;
  const effectiveBusinessId = userContext.businessId || DEFAULT_BUSINESS_ID;

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Requirements card state
  const [requirements, setRequirements] = useState([]);
  const [newRequirement, setNewRequirement] = useState("");
  const [expandedReqIds, setExpandedReqIds] = useState(new Set());
  const [pendingRequirementPhotos, setPendingRequirementPhotos] = useState([]);
  const [showRequirementPhotoModal, setShowRequirementPhotoModal] = useState(false);
  const [isRequirementUploading, setIsRequirementUploading] = useState(false);
  const [requirementPhotoCache, setRequirementPhotoCache] = useState({});
  const requirementStatuses = [
    "Wont fix",
    "Done",
    "Need Time",
    "Need Explanation",
    "Complex",
    "More manpower needed",
    "Ongoing",
  ];

  useEffect(() => {
    dispatch(fetchContacts());
    dispatch(fetchFiles({}));
  }, [dispatch]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await API.get('user/user/get-users');
        setUsers(response.data || []);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Load user requirements from backend
  // Derive current user id from auth or JWT token
  const decodeJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (_) {
      return null;
    }
  };
  const tokenUserId = (() => {
    const t = localStorage.getItem('accessToken');
    if (!t) return null;
    const payload = decodeJwt(t);
    return payload?.userId || null;
  })();
  const currentUserId =
    auth?.userData?.user?.id ||
    auth?.userData?.user?._id ||
    auth?.userData?.userId ||
    auth?.userData?._id ||
    auth?.userId ||
    tokenUserId ||
    null;
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsError, setRequirementsError] = useState(null);

  useEffect(() => {
    const fetchReqs = async () => {
      if (!currentUserId) return;
      setRequirementsLoading(true);
      setRequirementsError(null);
      try {
        const res = await API.get(`user/user/${currentUserId}`);
        setRequirements(res.data?.requirements || []);
      } catch (e) {
        console.error('Error fetching requirements', e);
        setRequirementsError('Failed to load requirements');
      } finally {
        setRequirementsLoading(false);
      }
    };
    fetchReqs();
  }, [currentUserId]);

  const totalStorage = useMemo(() => {
    return files.reduce((sum, f) => sum + (Number(f.fileSize) || 0), 0);
  }, [files]);

  const formatMB = (bytes) => `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch (e) {
      return iso;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "-";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = Math.max(bytes, 0);
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) {
      size /= 1024;
      idx += 1;
    }
    return `${size.toFixed(size < 10 && idx > 0 ? 1 : 0)} ${units[idx]}`;
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const uploadRequirementPhotos = async () => {
    if (!pendingRequirementPhotos.length) return [];
    if (!effectiveUserId || !effectiveBusinessId) throw new Error('Missing user or business context for attachments');
    setIsRequirementUploading(true);
    try {
      const uploadedIds = [];
      for (const file of pendingRequirementPhotos) {
        const base64 = await toBase64(file);
        const result = await dispatch(uploadFile({
          userId: effectiveUserId,
          businessId: effectiveBusinessId,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          dataBase64: base64,
          contactIds: [],
        })).unwrap();
        const fileId = result?._id || result?.id;
        if (fileId) uploadedIds.push(fileId);
      }
      setPendingRequirementPhotos([]);
      return uploadedIds;
    } finally {
      setIsRequirementUploading(false);
    }
  };

  const removePendingRequirementPhoto = (idx) => {
    setPendingRequirementPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const openRequirementPhoto = async (files = []) => {
    if (!Array.isArray(files) || files.length === 0) return;
    const ref = files[0];
    const fileId = typeof ref === 'object' ? (ref._id || ref.id) : ref;
    if (!fileId) return;
    const cached = requirementPhotoCache[fileId];
    if (cached?.url) {
      window.open(cached.url, '_blank', 'noopener');
      return;
    }
    try {
      const res = await dispatch(getFileById(fileId)).unwrap();
      const url = res.downloadUrl || res.fileUrl;
      if (!url) throw new Error('File unavailable');
      setRequirementPhotoCache((prev) => ({ ...prev, [fileId]: { url } }));
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      console.error('Failed to open requirement attachment', error);
      alert('Unable to open attachment');
    }
  };

  const addRequirement = async () => {
    const text = newRequirement.trim();
    if (!text || !currentUserId) return;
    try {
      const photoIds = await uploadRequirementPhotos();
      const body = { text };
      if (photoIds.length > 0) body.photoFiles = photoIds;
      const res = await API.post(`user/user/${currentUserId}/requirements`, body);
      const created = res.data?.result || null;
      if (created) setRequirements((prev) => [created, ...prev]);
      setNewRequirement("");
      setPendingRequirementPhotos([]);
    } catch (e) {
      console.error('Failed to add requirement', e);
      setRequirementsError('Failed to add requirement');
    }
  };

  const updateRequirementStatus = async (id, status) => {
    if (!currentUserId) return;
    try {
      const res = await API.patch(`user/user/${currentUserId}/requirements/${id}`, { status });
      const updated = res.data?.result;
      setRequirements((prev) => prev.map((r) => (r._id === id ? { ...r, status: updated?.status || status } : r)));
    } catch (e) {
      console.error('Failed to update requirement', e);
      setRequirementsError('Failed to update requirement');
    }
  };

  /* const deleteRequirement = async (id) => {
    if (!currentUserId) return;
    const ok = window.confirm('Delete this requirement?');
    if (!ok) return;
    try {
      await API.delete(`user/user/${currentUserId}/requirements/${id}`);
      setRequirements((prev) => prev.filter((r) => r._id !== id));
      setExpandedReqIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (e) {
      console.error('Failed to delete requirement', e);
      setRequirementsError('Failed to delete requirement');
    }
  }; */

  const toggleExpand = (id) => {
    setExpandedReqIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const needsClamp = (text) => (text || '').length > 120;

  const statusBadgeClass = (status) => {
    switch (status) {
      case "Done":
        return "badge bg-success";
      case "Wont fix":
        return "badge bg-secondary";
      case "Need Time":
        return "badge bg-warning text-dark";
      case "Need Explanation":
        return "badge bg-dark text-light";
      case "Complex":
        return "badge bg-danger";
      case "More manpower needed":
        return "badge bg-info text-dark";
      case "Ongoing":
      default:
        return "badge bg-primary";
    }
  };

  const recentContacts = useMemo(() => contacts.slice(0, 5), [contacts]);
  const recentFiles = useMemo(() => files.slice(0, 5), [files]);

  const busy = usersLoading || contactStatus === 'loading' || fileStatus === 'loading' || requirementsLoading;

  return (
    <Fragment>
      <div className="container">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Breadcrumb items={[{ label: 'Home', path: '/dashboard' }, { label: 'Dashboard' }]} />
          {busy ? <Loader size="sm" /> : null}
        </div>

        <div className="row g-3 mb-3">
          <div className="col-sm-6 col-lg-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted">Contacts</div>
                <div className="h3 mb-0">{contacts.length}</div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted">Files</div>
                <div className="h3 mb-0">{files.length}</div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted">Users</div>
                <div className="h3 mb-0">{users.length}</div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted">Storage Used</div>
                <div className="h3 mb-0">{formatMB(totalStorage)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-lg-6">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Contacts</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/contact')}>View All</button>
              </div>
              <div className="card-body">
                {recentContacts.length === 0 ? (
                  <div className="text-muted">No contacts found.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentContacts.map((c) => (
                          <tr key={c._id}>
                            <td>{c.contactName || '-'}</td>
                            <td>{c.contactEmail || '-'}</td>
                            <td>{c.contactPhone || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Files</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/file-store')}>View All</button>
              </div>
              <div className="card-body">
                {recentFiles.length === 0 ? (
                  <div className="text-muted">No files found.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Type</th>
                          <th className="text-end">Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentFiles.map((f) => (
                          <tr key={f._id || f.id}>
                            <td>{f.fileName || '-'}</td>
                            <td>{f.fileType || '-'}</td>
                            <td className="text-end">{typeof f.fileSize === 'number' ? formatMB(f.fileSize) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Requirements Card */}
        <div className="row g-3 mt-3">
          <div className="col-12">
            <div className="card h-100">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Requirements</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">New Requirement</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Write a new requirement..."
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                  />
                  <div className="mt-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="form-label mb-0">Photos</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setShowRequirementPhotoModal(true)}
                      >
                        Attach Photos
                      </button>
                    </div>
                    {pendingRequirementPhotos.length === 0 ? (
                      <div className="text-muted small">No photos selected.</div>
                    ) : (
                      <ul className="list-group list-group-flush small border rounded mt-1">
                        {pendingRequirementPhotos.map((file, idx) => (
                          <li
                            key={`${file.name}-${file.size}-${file.lastModified}-${idx}`}
                            className="list-group-item d-flex align-items-center justify-content-between py-2"
                          >
                            <div className="me-2 text-truncate">
                              <div className="fw-medium text-truncate" title={file.name}>{file.name}</div>
                              <div className="text-muted">{formatFileSize(file.size)}</div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removePendingRequirementPhoto(idx)}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="form-text">Photos upload when you submit the requirement.</div>
                  </div>
                  <div className="mt-2 d-flex justify-content-end">
                    <button
                      className="btn btn-primary"
                      onClick={addRequirement}
                      disabled={!newRequirement.trim() || !currentUserId || isRequirementUploading}
                    >
                      Submit Requirement
                    </button>
                  </div>
                </div>

                {requirementsError ? (
                  <div className="text-danger">{requirementsError}</div>
                ) : requirements.length === 0 ? (
                  <div className="text-muted">No requirements submitted yet{!currentUserId ? ' (login required)' : ''}.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: "44%" }}>Requirement</th>
                          <th style={{ width: "18%" }}>Submitted</th>
                          <th style={{ width: "18%" }}>Status</th>
                          <th style={{ width: "20%" }} className="text-center">Attachments</th>
                          {/* <th style={{ width: "10%" }}>Action</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {requirements.map((r) => (
                          <tr key={r._id}>
                            <td style={{ whiteSpace: "pre-wrap" }}>
                              <div className={expandedReqIds.has(r._id) ? '' : 'line-clamp-2'}>{r.text}</div>
                              {needsClamp(r.text) && (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 mt-1"
                                  onClick={() => toggleExpand(r._id)}
                                >
                                  {expandedReqIds.has(r._id) ? 'Read less' : 'Read more…'}
                                </button>
                              )}
                            </td>
                            <td>{formatDate(r.submittedAt)}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span className={statusBadgeClass(r.status)}>{r.status}</span>
                                <select
                                  className="form-select form-select-sm"
                                  value={r.status}
                                  onChange={(e) => updateRequirementStatus(r._id, e.target.value)}
                                >
                                  {requirementStatuses.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              </div>
                            </td>
                            <td className="text-center">
                              {Array.isArray(r.photoFiles) && r.photoFiles.length > 0 ? (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0"
                                  onClick={() => openRequirementPhoto(r.photoFiles)}
                                >
                                  View ({r.photoFiles.length})
                                </button>
                              ) : (
                                <span className="text-muted small">-</span>
                              )}
                            </td>
                            {/* <td>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => deleteRequirement(r._id)}
                              >
                                Delete
                              </button>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 d-flex gap-2">
          <button className="btn btn-primary" onClick={() => navigate('/contact')}>Add Contact</button>
          <button className="btn btn-outline-primary" onClick={() => navigate('/file-store')}>Go to File Store</button>
        </div>
      </div>
      <FileUploadModal
        show={showRequirementPhotoModal}
        onClose={() => setShowRequirementPhotoModal(false)}
        title="Attach Requirement Photos"
        helperText="Select one or more images related to this requirement."
        accept="image/*"
        selectedFiles={pendingRequirementPhotos}
        onFilesChange={setPendingRequirementPhotos}
        primaryActionLabel="Done"
        primaryDisabled={false}
        onPrimaryAction={() => setShowRequirementPhotoModal(false)}
      />
    </Fragment>
  );
}
